import { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import {
  InputAdornment,
  TextField,
  IconButton,
  Menu,
  MenuItem,
  Box,
  Button,
  ListItemText,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';
import { getAllVendorInvoicesApprovalPerson } from './functions/get_all_vendor_invoices_approval_person.function';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import ClearIcon from '@mui/icons-material/Clear';

const formatDate = (dateString) => {
  if (!dateString) {
    return 'N/A';
  }
  try {
    const date = new Date(dateString);
    return Number.isNaN(date.getTime())
      ? 'N/A'
      : date.toLocaleDateString('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        });
  } catch {
    return 'N/A';
  }
};

function AllInvoicesFullActionsMenu({ row, onView }) {
  const [anchorEl, setAnchorEl] = useState(null);
  return (
    <>
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          setAnchorEl(e.currentTarget);
        }}
        sx={{
          color: '#667085',
          '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
        }}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            onView(row.original);
          }}
          sx={{ gap: 0.5, '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
        >
          <VisibilityIcon fontSize="small" sx={{ mr: 0.5 }} />
          <ListItemText primary="View Details" />
        </MenuItem>
      </Menu>
    </>
  );
}

export const AllInvoicesFullTab = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [allInvoicesData, setAllInvoicesData] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchInvoices = async () => {
      setLoading(true);
      try {
        const response = await getAllVendorInvoicesApprovalPerson();
        setAllInvoicesData(Array.isArray(response) ? response : []);
      } catch (err) {
        setAllInvoicesData([]);
      } finally {
        setLoading(false);
      }
    };
    fetchInvoices();
  }, []);

  const filteredData = useMemo(() => {
    let filtered = allInvoicesData;

    if (dateFrom || dateTo) {
      filtered = filtered.filter((inv) => {
        const invDate = inv.created_at ? dayjs(inv.created_at) : null;
        if (!invDate || !invDate.isValid()) {
          return false;
        }
        const invDateStr = invDate.format('YYYY-MM-DD');
        if (dateFrom) {
          const fromStr = dayjs(dateFrom).format('YYYY-MM-DD');
          if (invDateStr < fromStr) {
            return false;
          }
        }
        if (dateTo) {
          const toStr = dayjs(dateTo).format('YYYY-MM-DD');
          if (invDateStr > toStr) {
            return false;
          }
        }
        return true;
      });
    }

    if (statusFilter && statusFilter.trim() !== '') {
      const statusLower = statusFilter.toLowerCase().trim();
      filtered = filtered.filter((inv) => (inv.status || '').toLowerCase() === statusLower);
    }

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(
        (inv) =>
          (inv.invoice_number || '').toLowerCase().includes(term) ||
          (inv.po_number || '').toLowerCase().includes(term) ||
          (inv.vendor || '').toLowerCase().includes(term) ||
          (inv.status || '').toLowerCase().includes(term)
      );
    }

    return filtered.map((inv) => {
      const created = inv.created_at ? dayjs(inv.created_at).format('YYYY-MM-DD') : 'N/A';
      const status = (inv.status || '').trim();
      const statusDisplay = status
        ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()
        : 'N/A';
      const amount =
        inv.invoice_amount != null
          ? Number(inv.invoice_amount).toLocaleString('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })
          : 'N/A';
      return {
        id: inv.id,
        invoiceNumber: inv.invoice_number || 'N/A',
        invoiceDate: created,
        poNumber: inv.po_number || 'N/A',
        vendor: inv.vendor || 'N/A',
        status: statusDisplay,
        invoiceAmount: amount,
        city: inv.po_city || inv.region || 'N/A',
        _originalData: inv,
      };
    });
  }, [allInvoicesData, dateFrom, dateTo, statusFilter, searchTerm]);

  const handleViewDetails = useCallback(
    (row) => {
      const inv = row._originalData || row;
      const invoiceNumber = inv.invoice_number || row.invoiceNumber;
      if (!invoiceNumber) {
        return;
      }
      navigate(`/approvalPersonsSystemInvoiceManagement/invoiceDetails/${invoiceNumber}`, {
        state: { invoiceData: inv, fromTab: 'all-invoices-full' },
      });
    },
    [navigate]
  );

  const columns = useMemo(
    () => [
      {
        accessorKey: 'invoiceNumber',
        header: 'Invoice Number',
        size: 150,
        enableColumnActions: false,
        Cell: ({ cell, row }) => (
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              handleViewDetails(row.original);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleViewDetails(row.original);
              }
            }}
            style={{
              fontSize: '14px',
              color: '#1976d2',
              cursor: 'pointer',
              textDecoration: 'underline',
            }}
          >
            {cell.getValue() || 'N/A'}
          </span>
        ),
      },
      {
        accessorKey: 'invoiceDate',
        header: 'Invoice Date',
        size: 120,
        enableColumnActions: false,
        Cell: ({ cell }) => (
          <span style={{ fontSize: '14px', color: '#667085' }}>{cell.getValue() || 'N/A'}</span>
        ),
      },
      {
        accessorKey: 'poNumber',
        header: 'PO Number',
        size: 140,
        enableColumnActions: false,
        Cell: ({ cell }) => (
          <span style={{ fontSize: '14px', color: '#667085' }}>{cell.getValue() || 'N/A'}</span>
        ),
      },
      {
        accessorKey: 'vendor',
        header: 'Vendor',
        size: 150,
        enableColumnActions: false,
        Cell: ({ cell }) => (
          <span style={{ fontSize: '14px', color: '#667085' }}>{cell.getValue() || 'N/A'}</span>
        ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        size: 110,
        enableColumnActions: false,
        Cell: ({ cell }) => (
          <span style={{ fontSize: '14px', color: '#667085' }}>{cell.getValue() || 'N/A'}</span>
        ),
      },
      {
        accessorKey: 'invoiceAmount',
        header: 'Invoice Amount',
        size: 130,
        enableColumnActions: false,
        Cell: ({ cell }) => (
          <span style={{ fontSize: '14px', color: '#667085' }}>{cell.getValue() ?? 'N/A'}</span>
        ),
      },
      {
        accessorKey: 'city',
        header: 'City',
        size: 120,
        enableColumnActions: false,
        Cell: ({ cell }) => (
          <span style={{ fontSize: '14px', color: '#667085' }}>{cell.getValue() || 'N/A'}</span>
        ),
      },
      {
        header: 'Actions',
        enableColumnActions: false,
        size: 50,
        Cell: ({ row }) => <AllInvoicesFullActionsMenu row={row} onView={handleViewDetails} />,
      },
    ],
    [handleViewDetails]
  );

  const handleDownloadExcel = useCallback(
    (tableInstance) => {
      const dataToDownload = tableInstance
        ? tableInstance.getFilteredRowModel().rows.map((r) => r.original)
        : filteredData;
      if (dataToDownload.length === 0) {
        return;
      }
      const excelData = dataToDownload.map((item) => ({
        'Invoice Number': item.invoiceNumber || '',
        'Invoice Date': item.invoiceDate || '',
        'PO Number': item.poNumber || '',
        Vendor: item.vendor || '',
        Status: item.status || '',
        'Invoice Amount': item.invoiceAmount || '',
        City: item.city || '',
      }));
      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'All Invoices');
      XLSX.writeFile(workbook, `All_Invoices_${dayjs().format('YYYY-MM-DD')}.xlsx`);
    },
    [filteredData]
  );

  const table = useMaterialReactTable({
    columns,
    data: filteredData,
    state: { isLoading: loading, pagination },
    onPaginationChange: setPagination,
    muiCircularProgressProps: { color: 'inherit', size: 40, sx: { color: '#999999' } },
    enableGlobalFilter: false,
    enableColumnFilters: true,
    enableSorting: false,
    enableRowSelection: false,
    enableDensityToggle: false,
    paginationDisplayMode: 'pages',
    manualPagination: false,
    muiPaginationProps: {
      color: 'secondary',
      rowsPerPageOptions: [10, 20, 30, 40],
      sx: {
        '& .MuiPaginationItem-root.Mui-selected': { backgroundColor: '#BD1D23', color: 'white' },
      },
    },
    enableBottomToolbar: true,
    enableStickyHeader: true,
    enablePagination: true,
    muiTableBodyRowProps: { sx: { height: 52, minHeight: 52 } },
    muiTableBodyCellProps: {
      sx: {
        color: '#667085',
        backgroundColor: '#fff',
        fontSize: '12px',
        padding: '0 12px',
      },
    },
    muiTableHeadCellProps: {
      sx: {
        color: '#344054',
        backgroundColor: '#E8E9E9',
        fontSize: '14px',
        fontWeight: 600,
        height: '56px',
        padding: '0 12px',
      },
    },
    muiTablePaperProps: { sx: { boxShadow: 'none' } },
    renderTopToolbarCustomActions: ({ table: t }) => (
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          alignItems: 'center',
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            placeholder="Search"
            size="small"
            variant="outlined"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{
              backgroundColor: '#fff',
              width: 300,
              borderRadius: '10px',
              '& .MuiOutlinedInput-notchedOutline': { border: '1px solid #ccc' },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: '#999' }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Invoice date from"
              value={dateFrom ? dayjs(dateFrom) : null}
              onChange={(v) => setDateFrom(v ? v.toDate() : null)}
              format="DD-MM-YYYY"
              slotProps={{
                textField: {
                  size: 'small',
                  InputLabelProps: { shrink: true },
                  sx: {
                    minWidth: 168,
                    backgroundColor: '#fff',
                    borderRadius: '10px',
                    '& .MuiOutlinedInput-notchedOutline': { border: '1px solid #ccc' },
                  },
                },
              }}
            />
            <DatePicker
              label="Invoice date to"
              value={dateTo ? dayjs(dateTo) : null}
              onChange={(v) => setDateTo(v ? v.toDate() : null)}
              format="DD-MM-YYYY"
              slotProps={{
                textField: {
                  size: 'small',
                  InputLabelProps: { shrink: true },
                  sx: {
                    minWidth: 168,
                    backgroundColor: '#fff',
                    borderRadius: '10px',
                    '& .MuiOutlinedInput-notchedOutline': { border: '1px solid #ccc' },
                  },
                },
              }}
            />
          </LocalizationProvider>
          {(dateFrom || dateTo) && (
            <IconButton
              size="small"
              onClick={() => {
                setDateFrom(null);
                setDateTo(null);
              }}
              sx={{
                padding: '4px',
                color: '#757575',
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                borderRadius: '50%',
                width: '24px',
                height: '24px',
                border: '1px solid #ccc',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.08)',
                  color: '#333',
                  border: '1px solid #999',
                },
              }}
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          )}
          <TextField
            select
            label="Status"
            size="small"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            InputLabelProps={{ shrink: true }}
            SelectProps={{
              displayEmpty: true,
              renderValue: (v) =>
                v ? v.charAt(0).toUpperCase() + v.slice(1).toLowerCase() : 'All statuses',
            }}
            sx={{
              minWidth: 168,
              backgroundColor: '#fff',
              borderRadius: '10px',
              '& .MuiOutlinedInput-notchedOutline': { border: '1px solid #ccc' },
            }}
          >
            <MenuItem value="">All statuses</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="approved">Approved</MenuItem>
            <MenuItem value="rejected">Rejected</MenuItem>
            <MenuItem value="stop">Stopped</MenuItem>
            <MenuItem value="reverted">Reverted</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
          </TextField>
          <Button
            variant="outlined"
            onClick={() => handleDownloadExcel(t)}
            disabled={filteredData.length === 0}
            sx={{
              border: '1px solid #ccc',
              color: '#333',
              borderRadius: '10px',
              padding: '8px',
              '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)', border: '1px solid #ccc' },
              '&:disabled': { color: '#999', opacity: 0.6 },
            }}
          >
            <DownloadIcon />
          </Button>
        </Box>
      </Box>
    ),
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
      <MaterialReactTable table={table} />
    </Box>
  );
};
